import React from "react";
import usePublicConfig from "storefront/hooks/usePublicConfig";
import findAlgoliaIndexName from "storefront/Config/PublicConfig/findAlgoliaIndexName";
import useAlgoliaClient from "./useAlgoliaClient";
import { normalizeQuery } from "./utils";

type RecentSearch = {
  type: "designer" | "query";
  label: string;
  value: string;
};

type RecentSearchWithTimestamp = RecentSearch & {
  lastSearchedAt?: string;
};

type RecentSearchHit = {
  query: string;
  nbHits: number;
};

type QueryToNewListings = {
  [query: string]: number;
};

const useRecentSearches = () => {
  const client = useAlgoliaClient();
  const publicConfig = usePublicConfig();

  const [recentSearches, setRecentSearches] = React.useState<
    Array<RecentSearchWithTimestamp>
  >([]);
  const [newListings, setNewListings] = React.useState<QueryToNewListings>({});

  React.useEffect(() => {
    const searches = localStorage.getItem("recentSearches");
    if (searches) {
      setRecentSearches(JSON.parse(searches));
    }
  }, []);

  React.useEffect(() => {
    if (!client || !publicConfig || recentSearches.length === 0) return;

    const listingIndex =
      findAlgoliaIndexName("listings", "created_at desc")(publicConfig) || "";

    if (!listingIndex) return;

    const fetchNewListings = async () => {
      const algoliaQueries = recentSearches.reduce<
        Array<{
          indexName: string;
          query: string;
          filters: string;
          params: Record<string, unknown>;
        }>
      >((acc, search) => {
        if (search.type !== "query" || !search.lastSearchedAt) return acc;

        const normalizedQuery = normalizeQuery(search.value);

        const lastSearchedAtI =
          new Date(search.lastSearchedAt).getTime() / 1000;
        const filterLastSearchedAt = lastSearchedAtI
          ? `created_at_i > ${lastSearchedAtI}`
          : "";

        if (!normalizedQuery || !filterLastSearchedAt) return acc;

        acc.push({
          indexName: listingIndex,
          query: normalizedQuery,
          filters: filterLastSearchedAt,
          params: {
            attributesToRetrieve: [],
            responseFields: ["nbHits", "query"],
          },
        });

        return acc;
      }, []);

      try {
        const { results } = await client.search<RecentSearchHit>(
          algoliaQueries,
        );

        const queryToNewListings = results.reduce((acc, res) => {
          return { ...acc, [res.query]: res.nbHits };
        }, {});

        setNewListings(queryToNewListings);
      } catch (err) {
        console.error(err);
      }
    };

    const debounceTimeout = setTimeout(fetchNewListings, 300);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [client, publicConfig, recentSearches]);

  const addSearch = React.useCallback(
    (search: RecentSearch) => {
      const duplicate = recentSearches.find((s) => s.label === search.label);
      if (duplicate) {
        return;
      }
      const newSearches = [
        { ...search, lastSearchedAt: new Date().toISOString() },
        ...recentSearches,
      ];
      setRecentSearches(newSearches);
      localStorage.setItem("recentSearches", JSON.stringify(newSearches));
    },
    [recentSearches],
  );

  const deleteSearch = React.useCallback(
    (search: RecentSearch) => {
      const newSearches = recentSearches.filter(
        (s) => s.label !== search.label,
      );
      setRecentSearches(newSearches);
      localStorage.setItem("recentSearches", JSON.stringify(newSearches));
    },
    [recentSearches],
  );

  const onSearch = React.useCallback(
    (search: RecentSearch) => {
      const updatedSearches = recentSearches.map((s) => {
        if (s.label === search.label) {
          return { ...s, lastSearchedAt: new Date().toISOString() };
        }
        return s;
      });
      setRecentSearches(updatedSearches);
      localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
    },
    [recentSearches],
  );

  return {
    recentSearches: recentSearches.slice(0, 5),
    newListings,
    addSearch,
    deleteSearch,
    onSearch,
  };
};

export default useRecentSearches;
